import React, { useLayoutEffect, useRef } from 'react'
import { alpha, Box, Tooltip, Typography } from '@mui/material'
import UidShorthand from 'src/legacy/components/UidShorthand'
import { ParticipantIdentity } from '../useParticipantName'
import { advancedColors, surfaces } from '../theme'
import { formatDay } from '../format'
import SubtitleFacts from './SubtitleFacts'
import PresenceLine from './PresenceLine'
import MedicalReportButton from './MedicalReportButton'
import { usePatientHeaderSize } from './PatientHeaderSize'

type Props = { uid: string; identity: ParticipantIdentity }

const Dot = () => (
  <Box component="span" sx={{ mx: 0.75 }}>
    ·
  </Box>
)

const IdentityHeader = ({ uid, identity }: Props) => {
  const headerRef = useRef<HTMLElement>(null)
  const { setHeight } = usePatientHeaderSize()

  useLayoutEffect(() => {
    const header = headerRef.current
    if (!header) {
      return undefined
    }
    const measure = () => setHeight(header.getBoundingClientRect().height)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(measure)
    observer.observe(header)
    return () => observer.disconnect()
  }, [uid, setHeight])

  return (
    <Box
      ref={headerRef}
      component="header"
      sx={{
        position: 'relative',
        zIndex: 1,
        flexShrink: 0,
        px: 2,
        pt: 2,
        pb: 1.5,
        backgroundColor: surfaces.green.bg,
        color: surfaces.green.fg,
        // The card list is a later sibling, so it would paint over this unless the header stacks above it.
        '&::before': {
          content: '""',
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 18,
          background: (theme) => `linear-gradient(to right, ${alpha(theme.palette.common.black, 0.06)}, transparent)`,
        },
        '&::after': {
          content: '""',
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: 18,
          background: (theme) => `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.06)}, transparent)`,
        },
        boxShadow: (theme) =>
          `0 1px 1px ${alpha(theme.palette.common.black, 0.08)}, 0 4px 10px ${alpha(theme.palette.common.black, 0.1)}`,
      }}
    >
      <Typography component="h1" variant="h2" sx={{ color: 'inherit', fontWeight: 600, wordBreak: 'break-word' }}>
        {identity.name}
      </Typography>
      {identity.preferredName && (
        <Typography
          component="p"
          variant="subtitle1"
          sx={{ mt: 0.25, mb: 0, color: advancedColors.inkMuted, fontStyle: 'italic' }}
        >
          Goes by {identity.preferredName}
        </Typography>
      )}
      <SubtitleFacts identity={identity} />
      <Typography component="p" variant="subtitle1" sx={{ mt: 0.25, mb: 0, color: advancedColors.inkMuted }}>
        {identity.programStartDate && (
          // `server`'s program date, not an Advanced track enrolment.
          <>
            <Box component="span">Program start</Box>{' '}
            <Box component="span" sx={{ color: surfaces.green.fg, fontWeight: 600 }}>
              {formatDay(identity.programStartDate)}
            </Box>
            <Dot />
          </>
        )}
        <Tooltip title={uid}>
          <Box component="span">
            <UidShorthand uid={uid} />
          </Box>
        </Tooltip>
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <PresenceLine uid={uid} />
        </Box>
        <MedicalReportButton uid={uid} />
      </Box>
    </Box>
  )
}

export default IdentityHeader
